/**
 * The back office refuses to run outside a secure context.
 *
 * ---------------------------------------------------------------------------
 * WHY A HARD STOP AND NOT A WARNING
 * ---------------------------------------------------------------------------
 *
 * This screen holds a session cookie and everything a park knows about its
 * guests. Served over plain HTTP, all of it — the cookie included — is readable
 * by anybody on the network between the operator and the server, and the
 * operator has no way to tell.
 *
 * A console warning would be seen by nobody: an operator does not open developer
 * tools, and the person who set the site up has already moved on. A banner would
 * be dismissed. The only message that reliably reaches somebody who can fix it
 * is the one that stops the page working, so this refuses to start and says what
 * to do.
 *
 * There is a technical reason too. A secure context is a precondition for
 * `crypto.subtle`, for clipboard access and for service workers, so a dashboard
 * on plain HTTP is a dashboard where features fail one at a time with unrelated
 * errors — which is a far worse afternoon than one clear refusal.
 *
 * ---------------------------------------------------------------------------
 * WHAT COUNTS AS SECURE
 * ---------------------------------------------------------------------------
 *
 * The browser decides, and `isSecureContext` is the answer. Notably it is TRUE
 * for `http://localhost` and `http://127.0.0.1`, because those never leave the
 * machine — so development needs no exemption from us, and we should not invent
 * one. Anything we added here would be a hole somebody widens.
 *
 * The guest widget does the OPPOSITE, deliberately: see the note in
 * `apps/guest/src/widget/mount.tsx`. It is embedded in a park's own site, which
 * we do not control, and refusing to render on their staging server would look
 * like our bug and be reported as one.
 */

/**
 * Stop, unless the browser says this context is secure.
 *
 * @param root Where to put the explanation. The app's own mount point, so the
 *             message lands where the interface would have been.
 * @returns whether it is safe to continue.
 */
export function requireSecureContext(root: HTMLElement): boolean {
  // `isSecureContext` is absent in a non-browser environment — a test, a
  // server-side render — and absent must not mean "refuse", or the suite
  // becomes untestable for a reason unrelated to what it is testing.
  if (typeof window === 'undefined' || window.isSecureContext !== false) return true;

  const proto = window.location.protocol;
  const suggestion = `https://${window.location.host}${window.location.pathname}`;

  // Built with the DOM rather than `innerHTML`: the host and path come from the
  // address bar, and an address bar can contain markup.
  const wrap = document.createElement('div');
  wrap.setAttribute('role', 'alert');
  wrap.style.cssText = 'max-width:34rem;margin:4rem auto;padding:1.5rem;'
    + 'font:16px/1.6 system-ui,sans-serif;border:1px solid #b32d2e;border-radius:8px';

  const title = document.createElement('h1');
  title.style.cssText = 'margin:0 0 .75rem;font-size:1.25rem';
  title.textContent = 'This page needs a secure connection';
  wrap.append(title);

  const why = document.createElement('p');
  why.style.margin = '0 0 .75rem';
  why.textContent = `It is being served over ${proto.replace(':', '')}, so the sign-in `
    + 'cookie and everything on these screens — guest names, addresses, prices — '
    + 'travel unencrypted and can be read by anyone on the network in between. '
    + 'The back office will not run like this.';
  wrap.append(why);

  const how = document.createElement('p');
  how.style.margin = '0 0 .75rem';
  how.append(document.createTextNode('Open it over https instead: '));
  const link = document.createElement('a');
  link.href = suggestion;
  link.textContent = suggestion;
  how.append(link);
  wrap.append(how);

  const note = document.createElement('p');
  note.style.cssText = 'margin:0;color:#555;font-size:.9rem';
  note.textContent = 'On a development machine, localhost and 127.0.0.1 already '
    + 'count as secure and need no certificate.';
  wrap.append(note);

  root.replaceChildren(wrap);
  return false;
}
